import { LocalStorageKey } from '@common/ts/enums';
import { getErrorMessage, getErrorStatus } from '@common/utils';
import useChatStore from 'chat-ui/src/store';
import { iframeLocalStorage } from 'chat-ui/src/utils/iframeLocalStorage';
import { StatusCodes } from 'http-status-codes';
import useChatSettingsStore from 'chat-ui/src/ChatSettingsStore';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ScreeningState } from '@/enums/enums';
import { ScreeningChatQuestion } from '@/types';
import { useMutatePreescreenProcess } from '@/reactQuery/post';
import axiosInstanceChat from '@/axios/axiosInstanceChat';

interface UseScreeningStateProps {
  screeningQuestions: ScreeningChatQuestion[];
  initialQuestionIndex: number;
  disqualificationRedirectUrl: string | null;
  quotaFullRedirectUrl: string | null;
  projectStorageSuffix?: string;
  projectLanguageLabel: string | null;
  basePath: string;
  projectId?: string;
  storedConversationId: string | null;
  chatId?: string;
  onScreenQuestionsEnd: (value: boolean) => void;
}

const useScreeningState = ({
  screeningQuestions,
  initialQuestionIndex,
  disqualificationRedirectUrl,
  quotaFullRedirectUrl,
  projectStorageSuffix,
  projectLanguageLabel,
  basePath,
  projectId,
  storedConversationId,
  chatId,
  onScreenQuestionsEnd,
}: UseScreeningStateProps) => {
  const location = useLocation();
  const { setCurrentConversationId } = useChatStore((state) => state);
  const navigate = useNavigate();
  const { setChatSettingsData } = useChatSettingsStore();
  const [searchParams] = useSearchParams();

  // Parse inputMethods from URL for override
  const urlInputMethods = useMemo(() => {
    const inputMethodsParam = searchParams.get('inputMethods');
    if (!inputMethodsParam) return null;
    const validMethods = ['text', 'audio', 'video', 'image'];
    const methodMap: Record<string, string> = { voice: 'audio' };
    const parsed = inputMethodsParam
      .split(',')
      .map((m) => m.trim().toLowerCase())
      .map((m) => methodMap[m] || m)
      .filter((m) => validMethods.includes(m));
    return parsed.length > 0 ? parsed : null;
  }, [searchParams]);

  // State variables
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(initialQuestionIndex ?? 0);
  const currentQuestion = screeningQuestions[activeQuestionIndex] as ScreeningChatQuestion;
  const [currentScreeningState, setCurrentScreeningState] = useState<ScreeningState>(
    ScreeningState.ONGOING,
  );
  const [disqualificationLink, setDisqualificationLink] = useState<string | null>(
    disqualificationRedirectUrl || null,
  );
  const [quotaFullLink, setQuotaFullLink] = useState<string | null>(
    quotaFullRedirectUrl || null,
  );
  const [initialAnswers, setInitialAnswers] = useState(currentQuestion?.responses);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);

  // Screening state mutation
  const {
    mutateAsync,
    isPending,
    data: prescreenDTO,
  } = useMutatePreescreenProcess(chatId, projectLanguageLabel, location.search.slice(1), basePath);

  const conversationId = storedConversationId ?? prescreenDTO?.conversation_id;

  const buildStorageKey = useCallback(
    (key: LocalStorageKey) => `${key}${projectStorageSuffix}`,
    [projectStorageSuffix],
  );

  // Function to finalize screening settings
  const finalizeScreeningSettings = (interviewType: string[]) => {
    // Use URL input methods override if provided, otherwise use project settings
    const finalInputMethods =
      urlInputMethods && urlInputMethods.length > 0 ? urlInputMethods : interviewType;
    iframeLocalStorage.setItem(
      `${LocalStorageKey.CHAT_SETTINGS}${projectId}`,
      finalInputMethods.join(','),
    );
    iframeLocalStorage.removeItem(
      `${LocalStorageKey.CURRENT_PROJECT_LANGUAGE}${projectStorageSuffix}`,
    );
    setChatSettingsData(finalInputMethods);
    onScreenQuestionsEnd(true);
  };

  // Function to complete screening and start chat
  // eslint-disable-next-line consistent-return
  const completeScreeningAndStartChat = async (fallbackConversationId: string) => {
    setIsLoading(true);
    try {
      const endpoint = `/chat/${basePath}${projectId}/${conversationId ?? fallbackConversationId}`;
      const res = await axiosInstanceChat.post(
        `${endpoint}?language=${projectLanguageLabel}&${location.search.slice(1)}`,
      );
      if (res?.data) {
        finalizeScreeningSettings(res?.data?.type_of_interview);
        setIsLoading(false);
        setCurrentConversationId(res?.data?.conversationId);
        iframeLocalStorage.removeItem(buildStorageKey(LocalStorageKey.SCREENING_QUESTION_INDEX));
      }
    } catch (error) {
      const errorStatus = getErrorStatus(error);
      const message = getErrorMessage(error);
      if (errorStatus === StatusCodes.CONFLICT) {
        // maximum amount of conversations exceeded
        iframeLocalStorage.removeItem(buildStorageKey(LocalStorageKey.SCREENING_STATE));
        return navigate(`/chat/error?error_status=${errorStatus}`);
      }
      toast.error(message);
      setIsLoading(false);
    }
  };

  const handleOngoingScreeningState = () => {
    iframeLocalStorage.setItem(
      buildStorageKey(LocalStorageKey.SCREENING_STATE),
      ScreeningState.ONGOING,
    );
    iframeLocalStorage.setItem(
      buildStorageKey(LocalStorageKey.SCREENING_QUESTION_INDEX),
      (activeQuestionIndex + 1).toString(),
    );
    setActiveQuestionIndex((prevQuestion) => prevQuestion + 1);
    setSelectedAnswers([]);
  };

  const handleQualifyScreeningState = async (conversation_id: string) => {
    iframeLocalStorage.setItem(
      buildStorageKey(LocalStorageKey.SCREENING_STATE),
      ScreeningState.QUALIFIED,
    );
    iframeLocalStorage.removeItem(buildStorageKey(LocalStorageKey.SCREENING_QUESTION_INDEX));
    await completeScreeningAndStartChat(conversation_id);
  };

  const handleDisqualifyScreeningState = (redirectionLink: string) => {
    setDisqualificationLink(redirectionLink);
    iframeLocalStorage.setItem(
      buildStorageKey(LocalStorageKey.SCREENING_STATE),
      ScreeningState.DISQUALIFIED,
    );
    iframeLocalStorage.setItem(
      buildStorageKey(LocalStorageKey.SCREENING_QUESTION_DISQUALIFY_LINK),
      redirectionLink,
    );
    iframeLocalStorage.removeItem(buildStorageKey(LocalStorageKey.SCREENING_QUESTION_INDEX));
  };

  const handleQuotaFullScreeningState = (redirectionLink: string) => {
    setQuotaFullLink(redirectionLink);
    iframeLocalStorage.setItem(
      buildStorageKey(LocalStorageKey.SCREENING_STATE),
      ScreeningState.QUOTA_FULL,
    );
    iframeLocalStorage.setItem(
      buildStorageKey(LocalStorageKey.SCREENING_QUESTION_QUOTA_FULL_LINK),
      redirectionLink,
    );
    iframeLocalStorage.removeItem(buildStorageKey(LocalStorageKey.SCREENING_QUESTION_INDEX));
  };

  const submitScreeningAnswer = async () => {
    const currentPayload = {
      question: currentQuestion?.question,
      answers: selectedAnswers,
      conversation_id: conversationId,
    };
    try {
      const response = await mutateAsync(currentPayload);
      const { state, conversation_id, link } = response;
      setCurrentScreeningState(state);

      switch (state) {
        case ScreeningState.ONGOING:
          handleOngoingScreeningState();
          break;

        case ScreeningState.QUALIFIED:
          handleQualifyScreeningState(conversation_id);
          break;

        case ScreeningState.DISQUALIFIED:
          handleDisqualifyScreeningState(link);
          break;

        case ScreeningState.QUOTA_FULL:
          handleQuotaFullScreeningState(link);
          break;

        default:
          console.warn(`Unhandled screening state: ${state}`);
          break;
      }

      setSelectedAnswers([]);
    } catch (error) {
      // handling error during the screening process
      const errorStatus = getErrorStatus(error);
      const message = getErrorMessage(error);
      const isLastQuestion = activeQuestionIndex === screeningQuestions.length - 1;
      // This is the status that indicates that user already answered current question
      if (errorStatus === StatusCodes.CONFLICT) {
        if (!isLastQuestion) {
          // move to next questions
          handleOngoingScreeningState();
        } else {
          // go to chat
          handleQualifyScreeningState(conversationId);
        }

        return;
      }
      toast.error(message);
    }
  };

  const handleSelectAnswer = (value: string, isMultiSelect: boolean) => {
    setSelectedAnswers((prev) => {
      if (isMultiSelect) {
        return prev.includes(value) ? prev.filter((answer) => answer !== value) : [...prev, value];
      }
      return [value];
    });
  };

  useEffect(() => {
    if (!screeningQuestions.length) return;
    iframeLocalStorage.setItem(
      buildStorageKey(LocalStorageKey.SCREENING_STATE),
      ScreeningState.ONGOING,
    );
  }, [buildStorageKey, screeningQuestions.length]);

  useEffect(() => {
    if (!conversationId) return;
    iframeLocalStorage.setItem(
      buildStorageKey(LocalStorageKey.CURRENT_CONVERSATION_ID),
      conversationId,
    );
  }, [conversationId, buildStorageKey]);

  return {
    activeQuestionIndex,
    currentScreeningState,
    disqualificationLink,
    quotaFullLink,
    currentQuestion,
    initialAnswers,
    isLoading,
    selectedAnswers,
    setInitialAnswers,
    handleSelectAnswer,
    submitScreeningAnswer,
    isPending,
  };
};

export default useScreeningState;
